import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  CircleCheckIcon,
  InfoIcon,
  OctagonXIcon,
  TriangleAlertIcon,
  XIcon,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "src/shadcn/lib/utils";

import { copyToClipboard } from "./utils";

type ToastrType = "success" | "info" | "warning" | "error";

const ICONS: Record<ToastrType, LucideIcon> = {
  success: CircleCheckIcon,
  info: InfoIcon,
  warning: TriangleAlertIcon,
  error: OctagonXIcon,
};

const VARIANT_CLASSES: Record<ToastrType, string> = {
  success:
    "bg-success-solid text-success-solid-foreground border-success-solid/30",
  error: "bg-error-solid text-error-solid-foreground border-error-solid/30",
  warning:
    "bg-warning-solid text-warning-solid-foreground border-warning-solid/30",
  info: "bg-info-solid text-info-solid-foreground border-info-solid/30",
};

export interface ToastrBodyProps {
  type: ToastrType;
  message: ReactNode;
  /** Plain-text version of the message; renders a copy-to-clipboard affordance when set. */
  copyableMessage?: string;
  showIcon?: boolean;
  dismissible?: boolean;
  buttonLabel?: string;
  onAction?: () => void;
  onDismiss: () => void;
}

const COPIED_FEEDBACK_DURATION_MS = 2000;

const ToastrBody = ({
  type,
  message,
  copyableMessage,
  showIcon = true,
  dismissible = true,
  buttonLabel,
  onAction,
  onDismiss,
}: ToastrBodyProps) => {
  const Icon = ICONS[type];
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(() => () => clearTimeout(copiedTimeoutRef.current), []);

  const handleCopy = async () => {
    if (!copyableMessage || !(await copyToClipboard(copyableMessage))) return;

    setIsCopied(true);
    clearTimeout(copiedTimeoutRef.current);
    copiedTimeoutRef.current = setTimeout(
      () => setIsCopied(false),
      COPIED_FEEDBACK_DURATION_MS
    );
  };

  return (
    <div
      role={type === "error" || type === "warning" ? "alert" : "status"}
      data-testid={`toastr-${type}-container`}
      className={cn(
        "flex w-full items-center gap-3 rounded-md border px-4 py-3 text-sm shadow-lg",
        VARIANT_CLASSES[type]
      )}
    >
      {showIcon && <Icon aria-hidden className="size-4 shrink-0" />}
      <span className="flex-1">
        {message}
        {copyableMessage && (
          <>
            {" "}
            <button
              type="button"
              data-testid={`toastr-${type}-copy-button`}
              className="text-xs italic underline-offset-2 opacity-80 transition-opacity hover:underline hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              onClick={handleCopy}
            >
              {isCopied
                ? t("neetoatoms.toastr.copied", "Copied!")
                : t("neetoatoms.toastr.copyMessage", "Copy this error message")}
            </button>
          </>
        )}
      </span>
      {buttonLabel && (
        <button
          type="button"
          onClick={onAction}
          data-testid={`toastr-${type}-button`}
          className="shrink-0 text-sm font-medium underline-offset-2 hover:underline"
        >
          {buttonLabel}
        </button>
      )}
      {dismissible && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          data-testid="toastr-close-button"
          className="shrink-0 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          <XIcon aria-hidden className="size-4" />
        </button>
      )}
    </div>
  );
};

export { ToastrBody };
